import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type {
  AnnouncementEntry,
  AnnouncementListParams,
  AnnouncementListResponseApi,
  CreateAnnouncementDto,
  UpdateAnnouncementDto,
  BulkDeleteAnnouncementsDto,
  AnnouncementListItemApi,
} from "../types/announcements.types";
import {
  apiStatusToUi,
  uiStatusToApi,
} from "../types/announcements.types";

const ENDPOINT = "/api/admin/announcements";

function formatPublishDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function mapListItemToEntry(item: AnnouncementListItemApi): AnnouncementEntry {
  const targetAudience =
    item.targetAudience?.trim() ||
    (item.targetAllStaff
      ? i18n.t("announcementsApi.allPersonnel")
      : item.departments?.length
        ? item.departments.map((d) => d.name).join(", ")
        : "—");
  const apiStatus = apiStatusToUi(item.status);
  // Yayında olduğu söylenen ama endDate'i geçmiş olan duyuruları
  // "Süresi dolmuş" olarak ele alıyoruz; backend bayrağı güncelleyene kadar
  // listede yanlış "Yayında" görünmesin.
  const endDateIso = item.endDate ?? null;
  const isExpired =
    apiStatus === "yayinda" &&
    !!endDateIso &&
    !Number.isNaN(Date.parse(endDateIso)) &&
    Date.parse(endDateIso) < Date.now();
  return {
    id: item.id,
    title: item.title,
    content: item.description ?? "",
    targetAudience,
    publishDate: formatPublishDate(item.publishDate ?? item.startDate ?? item.createdAt ?? new Date().toISOString()),
    status: isExpired ? "suresi_dolmus" : apiStatus,
    endDateIso,
    imageFileID: item.imageFileID ?? null,
  };
}

export const announcementsApi = {
  /**
   * GET /api/admin/announcements - List with pagination and filters.
   */
  getAll: async (
    params?: AnnouncementListParams
  ): Promise<{ data: AnnouncementEntry[]; total: number }> => {
    const query: Record<string, unknown> = {};
    if (params?.page != null) query.page = params.page;
    if (params?.limit != null) query.limit = params.limit;
    if (params?.search) query.search = params.search;
    if (params?.status !== undefined) query.status = params.status;
    if (params?.targetDepartmentIds?.length)
      query.targetDepartmentIds = params.targetDepartmentIds;
    if (params?.targetEducationIds?.length)
      query.targetEducationIds = params.targetEducationIds;
    if (params?.dateFrom) query.dateFrom = params.dateFrom;
    if (params?.dateTo) query.dateTo = params.dateTo;
    if (params?.sortBy) query.sortBy = params.sortBy;
    if (params?.sortOrder) query.sortOrder = params.sortOrder;

    const { data } = await apiClient.get<AnnouncementListResponseApi>(
      ENDPOINT,
      { params: query }
    );
    const list = data?.data ?? [];
    const total = data?.count ?? data?.total ?? list.length;
    return {
      data: list.map(mapListItemToEntry),
      total,
    };
  },

  /**
   * GET /api/admin/announcements/{id}
   */
  getById: async (id: string): Promise<AnnouncementListItemApi> => {
    const { data } = await apiClient.get<{
      success?: boolean;
      data?: AnnouncementListItemApi;
    }>(`${ENDPOINT}/${id}`);
    if (!data?.data) throw new Error(i18n.t("announcementsApi.notFound"));
    return data.data;
  },

  /**
   * POST /api/admin/announcements - Create announcement.
   */
  create: async (body: CreateAnnouncementDto): Promise<AnnouncementListItemApi> => {
    const { data } = await apiClient.post<{
      success?: boolean;
      data?: AnnouncementListItemApi;
    }>(ENDPOINT, body);
    if (!data?.data) throw new Error(i18n.t("announcementsApi.createFailed"));
    return data.data;
  },

  /**
   * PATCH /api/admin/announcements/{id}
   */
  update: async (
    id: string,
    body: UpdateAnnouncementDto
  ): Promise<AnnouncementListItemApi> => {
    const { data } = await apiClient.patch<{
      success?: boolean;
      data?: AnnouncementListItemApi;
    }>(`${ENDPOINT}/${id}`, body);
    if (!data?.data) throw new Error(i18n.t("announcementsApi.updateFailed"));
    return data.data;
  },

  /**
   * DELETE /api/admin/announcements/{id}
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${ENDPOINT}/${id}`);
  },

  /**
   * PATCH /api/admin/announcements/{id}/unpublish - Set status to UNPUBLISHED.
   */
  unpublish: async (id: string): Promise<void> => {
    await apiClient.patch(`${ENDPOINT}/${id}/unpublish`);
  },

  /**
   * PATCH /api/admin/announcements/bulk/unpublish - Bulk unpublish announcements.
   */
  bulkUnpublish: async (ids: string[]): Promise<void> => {
    await apiClient.patch(`${ENDPOINT}/bulk/unpublish`, { ids });
  },

  /**
   * DELETE /api/admin/announcements/bulk
   */
  bulkDelete: async (ids: string[]): Promise<void> => {
    await apiClient.delete(`${ENDPOINT}/bulk`, {
      data: { ids } as BulkDeleteAnnouncementsDto,
    });
  },
};

export { uiStatusToApi, apiStatusToUi };
